import * as React from 'react';

import { X } from 'lucide-react';
import { cn } from '../../../lib';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: React.ReactNode;
  clearable?: boolean;
  onClear?: () => void;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ icon, className, clearable, onClear, onFocus, onBlur, ...props }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false);

    if (icon) {
      return (
        // Container anchors both the bubble and the sliding input wrapper
        <div className={cn('relative h-10 w-full', className)}>
          {/* Bubble — grows from the left edge as the input slides away */}
          <div
            aria-hidden="true"
            className={cn(
              'absolute left-0 top-0 flex h-10 items-center justify-center overflow-hidden rounded-full border bg-background',
              'transition-all duration-300 ease-out',
              isFocused
                ? 'w-10 border-primary bg-primary opacity-100 shadow-[0_0_0_3px_hsl(var(--primary)/0.15),0_4px_14px_hsl(var(--primary)/0.25)]'
                : 'w-0 border-transparent opacity-0',
            )}
          >
            <span
              className={cn(
                'transition-colors duration-300',
                isFocused ? 'text-primary-foreground' : 'text-muted-foreground',
              )}
            >
              {icon}
            </span>
          </div>

          {/* Input wrapper — left edge slides right to "split" from the bubble */}
          <div
            className={cn(
              'absolute right-0 top-0 border bg-background shadow-sm transition-all duration-300 ease-out ring-offset-background',
              isFocused
                ? 'left-12 rounded-full border-primary ring-2 ring-ring ring-offset-2'
                : 'left-0 rounded-lg border-input',
            )}
          >
            {/* Inline icon — fades out as the bubble takes its place */}
            <span
              aria-hidden="true"
              className={cn(
                'absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none transition-opacity duration-150',
                isFocused ? 'opacity-0' : 'opacity-100',
              )}
            >
              {icon}
            </span>

            <InputElement
              {...props}
              ref={ref}
              onFocus={e => {
                setIsFocused(true);
                onFocus?.(e);
              }}
              onBlur={e => {
                setIsFocused(false);
                onBlur?.(e);
              }}
              className={cn(
                'w-full border-0 bg-transparent shadow-none transition-all duration-300 ease-out focus:ring-0 focus:ring-offset-0 focus:border-transparent focus-visible:ring-0 focus-visible:ring-offset-0',
                isFocused ? 'rounded-full pl-4' : 'pl-10',
                clearable && 'pr-10',
              )}
            />

            {clearable && props.value && (
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                onClick={() => {
                  props.onChange?.({
                    target: { value: '' },
                  } as React.ChangeEvent<HTMLInputElement>);
                  onClear?.();
                }}
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      );
    }

    if (clearable) {
      return (
        <div className={cn('relative', className)}>
          <InputElement {...props} className="pr-10" ref={ref} />
          {props.value && (
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              onClick={() => {
                props.onChange?.({
                  target: { value: '' },
                } as React.ChangeEvent<HTMLInputElement>);
                onClear?.();
              }}
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      );
    }

    return <InputElement ref={ref} className={className} {...props} />;
  },
);
Input.displayName = 'Input';

const InputElement = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, onBlur, onChange, ...props }, ref) => {
    return (
      <input
        className={cn(
          // text-base on mobile: iOS Safari/Samsung Internet zoom the viewport on focus of inputs < 16px
          'flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-base md:text-sm shadow-sm transition-all ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:border-primary focus:rounded-full disabled:cursor-not-allowed disabled:opacity-95',
          className,
          props.disabled && 'bg-gray-50',
        )}
        ref={ref}
        {...props}
        onChange={onChange}
        onBlur={e => {
          // Never trim passwords: leading/trailing spaces are legal characters there
          const trimmed = e.target.value.trim();
          if (props.type !== 'password' && trimmed !== e.target.value) {
            e.target.value = trimmed;
            onChange?.(e);
          }
          onBlur?.(e);
        }}
        onWheel={(e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur()}
      />
    );
  },
);
InputElement.displayName = 'InputElement';

export { Input };
